import React, { useState } from 'react';
import {
  Input,
  Typography,
  FormControl,
  FormLabel,
  Button,
  Box,
  Sheet,
  Alert
} from '@mui/joy';
import WarningIcon from '@mui/icons-material/Warning';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useNavigate, useLocation } from 'react-router-dom';
import { createUser, getCurrentUser } from '../../../apiconfig/api';
import { useSession } from 'context/SessionContext';

interface FormValues {
  fullName: string;
  email: string;
  password: string;
  companyName?: string;
}

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector((state: RootState) => state.auth.loading);
  const { setSessionEmployerId } = useSession();

  // Get URL parameters
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');
  const isNewEmployer = searchParams.get('newEmployer') === 'true';
  const companyName = searchParams.get('companyName') || '';

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const formik = useFormik<FormValues>({
    initialValues: {
      fullName: '',
      email: '',
      password: '',
      companyName: companyName,
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required('Full name is required'),
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Password is required'),
      ...(isNewEmployer && {
        companyName: Yup.string().required('Company name is required'),
      }),
    }),
    onSubmit: async (values) => {
      try {
        setErrorMessage('');

        // Create user
        const userResponse = await createUser({
          fullName: values.fullName,
          email: values.email,
          password: values.password,
          ...(isNewEmployer && {
            companyName: values.companyName,
          }),
        });

        // Store the token in localStorage
        if (userResponse.data.token) {
          localStorage.setItem('session_token', userResponse.data.token);
        }

        // Set sessionEmployerId after login
        try {
          const userResp = await getCurrentUser();
          const user = userResp.data.user;
          // If user has an employers array, use the first one; else fallback to user id
          if (user.employers && Array.isArray(user.employers) && user.employers.length > 0) {
            setSessionEmployerId(user.employers[0].uuid);
          }
        } catch (e) {
          // fallback: clear sessionEmployerId
          setSessionEmployerId(null);
        }

        // Show success state
        setIsSuccess(true);

        // Navigate to redirect URL or dashboard after showing success message
        setTimeout(() => {
          navigate(redirectUrl || '/hiring/positions');
        }, 2000);

      } catch (error: any) {
        console.error('Error during sign-up:', error);
        
        // Extract error message from backend response
        let errorMsg = 'Sign-up failed. Please try again.';
        
        if (error.message) {
          // If it's already a string message from the backend
          errorMsg = error.message;
        } else if (error.response?.data?.message) {
          // If it's an axios-style error with response data
          errorMsg = error.response.data.message;
        } else if (typeof error === 'string') {
          // If error is directly a string
          errorMsg = error;
        }
        
        setErrorMessage(errorMsg);
      }
    }
  });

  const { values, handleChange, handleSubmit, touched, errors, handleBlur } = formik;

  const inputStyles = {
    height: '56px',
    backgroundColor: '#ECEFF3',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.125rem',
    color: '#1A1B1D',
    '--Input-focusedHighlight': 'transparent !important',
    '&::placeholder': {
      color: '#7B7F85 !important',
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: '#ECEFF3',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      backgroundColor: '#ECEFF3',
    },
  };

  if (isSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f4f4',
          padding: '20px',
        }}
      >
        <Sheet
          sx={{
            width: '100%',
            maxWidth: '480px',
            padding: '40px',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
          }}
        >
          <Typography 
            level="h1" 
            fontSize="28px" 
            sx={{ 
              fontWeight: 600, 
              color: '#1A1B1D',
              marginBottom: '16px'
            }}
          >
            You're in! Welcome to GeekApp.
          </Typography>
          <Typography 
            level="body-md" 
            sx={{ 
              color: '#7B7F85',
              fontSize: '16px'
            }}
          >
            Redirecting you...
          </Typography>
        </Sheet>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f4f4',
        padding: '20px',
      }}
    >
      <Sheet
        sx={{
          width: '100%',
          maxWidth: '480px',
          padding: '40px',
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <form onSubmit={handleSubmit}>
          <Typography 
            level="h1" 
            marginBottom="32px" 
            fontSize="28px" 
            sx={{ 
              fontWeight: 600, 
              color: '#1A1B1D',
              textAlign: 'center'
            }}
          >
            {isNewEmployer ? 'Create Employer Account' : 'Create Account'}
          </Typography>

          {errorMessage && (
            <Alert 
              color="danger" 
              startDecorator={<WarningIcon />}
              sx={{ 
                marginBottom: '24px',
                borderRadius: '8px'
              }}
            >
              {errorMessage}
            </Alert>
          )}

          {isNewEmployer && (
            <FormControl error={touched.companyName && Boolean(errors.companyName)} sx={{ marginBottom: '24px' }}>
              <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                Company Name
              </FormLabel>
              <Input
                name="companyName"
                value={values.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Your company name"
                autoComplete="organization"
                sx={inputStyles}
              />
              {touched.companyName && errors.companyName && (
                <Typography level="body-sm" color="danger" marginTop="5px">
                  {errors.companyName}
                </Typography>
              )}
            </FormControl>
          )}

          <FormControl error={touched.fullName && Boolean(errors.fullName)} sx={{ marginBottom: '24px' }}>
            <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
              Full Name
            </FormLabel>
            <Input
              name="fullName"
              value={values.fullName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Your full name"
              autoComplete="name"
              sx={inputStyles}
            />
            {touched.fullName && errors.fullName && (
              <Typography level="body-sm" color="danger" marginTop="5px">
                {errors.fullName}
              </Typography>
            )}
          </FormControl>

          <FormControl error={touched.email && Boolean(errors.email)} sx={{ marginBottom: '24px' }}>
            <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
              Email Address
            </FormLabel>
            <Input
              type="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="your.email@example.com"
              autoComplete="email"
              sx={inputStyles}
            />
            {touched.email && errors.email && (
              <Typography level="body-sm" color="danger" marginTop="5px">
                {errors.email}
              </Typography>
            )}
          </FormControl>

          <FormControl error={touched.password && Boolean(errors.password)} sx={{ marginBottom: '48px' }}>
            <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
              Password
            </FormLabel>
            <Input
              type="password"
              name="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Create a password"
              autoComplete="new-password"
              sx={inputStyles}
            />
            {touched.password && errors.password && (
              <Typography level="body-sm" color="danger" marginTop="5px">
                {errors.password}
              </Typography>
            )}
          </FormControl>

          <Button
            variant="solid"
            fullWidth
            type="submit"
            loading={loading}
            sx={{
              height: '56px',
              fontSize: '18px',
              fontWeight: 600,
              marginBottom: '24px',
            }}
          >
            Sign Up
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              level="body-sm" 
              sx={{ 
                color: '#7B7F85', 
                fontSize: '16px',
                fontWeight: 400
              }}
            >
              Already have an account?{' '}
              <Typography
                component="span"
                sx={{
                  color: '#2e2d7e',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => navigate(`/auth/signin${location.search}`)}
              >
                Sign in
              </Typography>
            </Typography>
          </Box>
        </form>
      </Sheet>
    </Box>
  );
};

export default SignUpPage;
